import { useQuery } from "@tanstack/react-query";
import { reverseGeocode } from "common/apis/maps";

import type { OnboardingBusinessMatch, OnboardingCity } from "../types";

type AddressComponent = {
  long_name: string;
  types: string[];
};

/**
 * Most specific first. London geocodes to a `postal_town`, not a `locality`,
 * so stopping at `locality` would leave UK businesses with no city at all.
 */
const CITY_TYPES = [
  "locality",
  "postal_town",
  "administrative_area_level_2",
  "administrative_area_level_1",
];

const pick = (components: AddressComponent[], types: string[]) => {
  for (const type of types) {
    const match = components.find((c) => c.types.includes(type));
    if (match?.long_name) return match.long_name;
  }
  return undefined;
};

/**
 * Derives "London, United Kingdom" from the claimed business's coordinates,
 * for "Let’s explore!" — the web port of native's `useBusinessCity`.
 *
 * Coordinates rather than anything else the claim holds, because they are
 * the one thing every lookup outcome carries: an in-database venue, a
 * Google-only place and an unsupported category all come back with lat/lng.
 *
 * `null`, not an error, when there is nothing to derive from — a business name
 * kept as typed never resolved to a place. The step then asks outright.
 */
export function useBusinessCity(business: OnboardingBusinessMatch | undefined) {
  const latitude = business?.latitude;
  const longitude = business?.longitude;
  const hasCoordinates = latitude != null && longitude != null;

  const { data, isLoading } = useQuery({
    queryKey: ["onboardingBusinessCity", latitude, longitude],
    enabled: hasCoordinates,
    // The business does not move, and a point does not change city.
    staleTime: Infinity,
    retry: 1,
    queryFn: async (): Promise<OnboardingCity | null> => {
      const response = await reverseGeocode(latitude!, longitude!);
      const components = (response.data?.results?.[0]?.address_components ??
        []) as AddressComponent[];

      const name = pick(components, CITY_TYPES);
      if (!name) return null;

      return {
        name,
        country: pick(components, ["country"]) ?? "",
        latitude: latitude!,
        longitude: longitude!,
        label: name,
      };
    },
  });

  return {
    city: data ?? null,
    // Nothing to wait for when there were no coordinates to begin with.
    isLoading: hasCoordinates && isLoading,
  };
}
